import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "2rem auto",
    padding: "1rem",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    color: "#1a202c",
  },
  hero: {
    background: "linear-gradient(to right, #2a4365, #434190)",
    color: "white",
    padding: "2rem",
    borderRadius: "0.75rem",
    marginBottom: "2rem",
    position: "relative",
    overflow: "hidden",
  },
  heroOverlay: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  heroContent: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  heroContentMd: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  profileImage: {
    width: "144px",
    height: "144px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "4px solid white",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    marginBottom: "1rem",
    marginRight: "1rem",
  },
  defaultProfile: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(to right, #4299e1, #667eea)",
    fontSize: "2rem",
    fontWeight: "bold",
  },
  heading: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    marginBottom: "1rem",
    textAlign: "center",
  },
  subInfo: {
    backgroundColor: "rgba(66, 153, 225, 0.5)",
    borderRadius: "9999px",
    padding: "0.5rem 1rem",
    display: "inline-block",
    marginBottom: "1rem",
  },
  tabNav: {
    display: "flex",
    borderBottom: "1px solid #e2e8f0",
    marginBottom: "1.5rem",
  },
  tabButton: (active) => ({
    flex: 1,
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: active ? "#3182ce" : "transparent",
    color: active ? "white" : "#4a5568",
    border: "none",
    transition: "background-color 0.2s",
  }),
  contentBox: {
    backgroundColor: "#ffffff",
    borderRadius: "0.5rem",
    padding: "1rem",
    border: "1px solid #e2e8f0",
  },
  errorBox: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    padding: "1rem",
    borderRadius: "0.5rem",
    maxWidth: "400px",
    margin: "2rem auto",
    textAlign: "center",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  spinner: {
    width: "64px",
    height: "64px",
    border: "4px solid #3182ce",
    borderTop: "4px solid transparent",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  // Add keyframes for spinner animation in a style tag (or in your global CSS)
};

function PlayerViewPage() {
  const { teamId, playerId } = useParams();

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summaryStats, setSummaryStats] = useState({
    points: 0,
    rebounds: 0,
    assists: 0,
    steals: 0,
    blocks: 0,
    games: 0,
  });
  const [recentGames, setRecentGames] = useState([]);
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    const fetchPlayerAndStats = async () => {
      try {
        // Fetch player basic info
        const playerRef = doc(db, "players", playerId);
        const playerSnap = await getDoc(playerRef);
        if (!playerSnap.exists()) {
          setError("Player not found.");
          setLoading(false);
          return;
        }
        const playerData = playerSnap.data();
        setPlayer(playerData);

        // Fetch ALL playerStats docs for this player
        const statsQuery = query(
          collection(db, "playerStats"),
          where("playerId", "==", playerId)
        );
        const statsSnap = await getDocs(statsQuery);

        // Track games and their data for this team
        const validStatsDocs = [];
        const gamesData = [];

        for (const statDoc of statsSnap.docs) {
          const data = statDoc.data();
          if (!data.gameId) continue;

          const gameRef = doc(db, "games", data.gameId);
          const gameSnap = await getDoc(gameRef);
          if (!gameSnap.exists()) continue;

          const gameData = gameSnap.data();
          const isHomeTeam = gameData.homeTeamId === teamId;
          const isAwayTeam = gameData.awayTeamId === teamId;

          if (isHomeTeam || isAwayTeam) {
            validStatsDocs.push(statDoc);
            gamesData.push({
              ...data,
              gameDate: gameData.date || gameData.timestamp?.toDate() || new Date(),
              gameId: data.gameId,
              opponent: isHomeTeam ? gameData.awayTeamName : gameData.homeTeamName,
              result: isHomeTeam 
                ? (gameData.homeScore > gameData.awayScore ? "W" : "L") 
                : (gameData.awayScore > gameData.homeScore ? "W" : "L"),
              score: isHomeTeam 
                ? `${gameData.homeScore || 0}-${gameData.awayScore || 0}` 
                : `${gameData.awayScore || 0}-${gameData.homeScore || 0}`
            });
          }
        }

        gamesData.sort((a, b) => b.gameDate - a.gameDate);
        setRecentGames(gamesData.slice(0, 5));

        const aggregatedStats = validStatsDocs.reduce(
          (acc, statDoc) => {
            const s = statDoc.data();
            return {
              points: acc.points + (s.points || 0),
              rebounds: acc.rebounds + (s.rebounds || 0),
              assists: acc.assists + (s.assists || 0),
              steals: acc.steals + (s.steals || 0),
              blocks: acc.blocks + (s.blocks || 0),
              games: acc.games + 1,
            };
          },
          { points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0, games: 0 }
        );
        setSummaryStats(aggregatedStats);
      } catch (err) {
        console.error("Error fetching player or stats:", err);
        setError("Error fetching player data.");
      }
      setLoading(false);
    };

    fetchPlayerAndStats();
  }, [teamId, playerId]);

  const calculateAvg = (stat) => {
    if (!summaryStats.games) return 0;
    return (stat / summaryStats.games).toFixed(1);
  };

  if (loading) {
    return (
      <div style={styles.loadingBox}>
        <div style={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorBox}>
        <h3>Error</h3>
        <p>{error}</p>
        <Link to={`/team/${teamId}`} style={{ color: "#3182ce", textDecoration: "underline" }}>
          Return to Team
        </Link>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Player Header with Hero Design */}
      <div style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={{ ...styles.heroContent, ...styles.heroContentMd }}>
          <div>
            {player.photoURL ? (
              <img 
                src={player.photoURL} 
                alt={`${player.firstName} ${player.lastName}`} 
                style={styles.profileImage}
              />
            ) : (
              <div style={{ ...styles.profileImage, ...styles.defaultProfile }}>
                {player.firstName?.[0]}{player.lastName?.[0]}
              </div>
            )}
          </div>
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <h1 style={styles.heading}>
              {player.firstName} {player.lastName}
            </h1>
            <div style={styles.subInfo}>
              <span>{player.position || 'No position specified'}</span>
              {player.jerseyNumber && (
                <>
                  <span style={{ margin: "0 0.5rem" }}>|</span>
                  <span style={{ fontWeight: "bold" }}>#{player.jerseyNumber}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs Navigation */}
      <div style={styles.tabNav}>
        {["profile", "stats", "games"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={styles.tabButton(activeTab === tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div style={styles.contentBox}>
        {activeTab === "profile" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
            <div style={{ backgroundColor: "#f7fafc", padding: "1rem", borderRadius: "0.5rem" }}>
              <h3 style={{ marginBottom: "1rem", fontSize: "1.125rem", fontWeight: "600", color: "#2d3748" }}>Personal Information</h3>
              <p><strong>Email:</strong> {player.email || 'N/A'}</p>
              <p><strong>Phone:</strong> {player.phone || 'N/A'}</p>
              <p><strong>Birthday:</strong> {player.birthday || 'N/A'}</p>
              <p><strong>Gender:</strong> {player.gender || 'N/A'}</p>
            </div>
            <div style={{ backgroundColor: "#f7fafc", padding: "1rem", borderRadius: "0.5rem" }}>
              <h3 style={{ marginBottom: "1rem", fontSize: "1.125rem", fontWeight: "600", color: "#2d3748" }}>Team Information</h3>
              <p><strong>Position:</strong> {player.position || 'N/A'}</p>
              <p><strong>Jersey #:</strong> {player.jerseyNumber || 'N/A'}</p>
              <p><strong>Relationship:</strong> {player.relationship || 'N/A'}</p>
              <p><strong>Notes:</strong> {player.notes || 'N/A'}</p>
            </div>
          </div>
        )}
        {activeTab === "stats" && (
          <div>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#2d3748" }}>Season Totals</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
              <div style={{ background: "linear-gradient(to bottom right, #ebf8ff, #edf2f7)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3182ce" }}>{summaryStats.points}</div>
                <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>Points</div>
              </div>
              <div style={{ background: "linear-gradient(to bottom right, #ebf8ff, #edf2f7)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3182ce" }}>{summaryStats.rebounds}</div>
                <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>Rebounds</div>
              </div>
              <div style={{ background: "linear-gradient(to bottom right, #ebf8ff, #edf2f7)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3182ce" }}>{summaryStats.assists}</div>
                <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>Assists</div>
              </div>
              <div style={{ background: "linear-gradient(to bottom right, #ebf8ff, #edf2f7)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3182ce" }}>{summaryStats.steals}</div>
                <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>Steals</div>
              </div>
              <div style={{ background: "linear-gradient(to bottom right, #ebf8ff, #edf2f7)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3182ce" }}>{summaryStats.blocks}</div>
                <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>Blocks</div>
              </div>
            </div>
            {summaryStats.games > 0 && (
              <div style={{ marginTop: "2rem" }}>
                <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#2d3748" }}>Per Game Averages</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
                  <div style={{ background: "linear-gradient(to bottom right, #edf2f7, #ebf8ff)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#4a5568" }}>{calculateAvg(summaryStats.points)}</div>
                    <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>PPG</div>
                  </div>
                  <div style={{ background: "linear-gradient(to bottom right, #edf2f7, #ebf8ff)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#4a5568" }}>{calculateAvg(summaryStats.rebounds)}</div>
                    <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>RPG</div>
                  </div>
                  <div style={{ background: "linear-gradient(to bottom right, #edf2f7, #ebf8ff)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#4a5568" }}>{calculateAvg(summaryStats.assists)}</div>
                    <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>APG</div>
                  </div>
                  <div style={{ background: "linear-gradient(to bottom right, #edf2f7, #ebf8ff)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#4a5568" }}>{calculateAvg(summaryStats.steals)}</div>
                    <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>SPG</div>
                  </div>
                  <div style={{ background: "linear-gradient(to bottom right, #edf2f7, #ebf8ff)", padding: "1rem", borderRadius: "0.5rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#4a5568" }}>{calculateAvg(summaryStats.blocks)}</div>
                    <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>BPG</div>
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginTop: "2rem", textAlign: "center" }}>
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "9999px",
                backgroundColor: "#ebf8ff",
                padding: "0.75rem 1.5rem",
                color: "#2b6cb0"
              }}>
                <span style={{ marginRight: "0.5rem" }}>Stats from {summaryStats.games} game{summaryStats.games !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === "games" && (
          <div>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#2d3748" }}>Recent Games</h3>
            {recentGames.length > 0 ? (
              <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "0.5rem" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ backgroundColor: "#f7fafc" }}>
                    <tr>
                      <th style={{ padding: "0.75rem", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Date</th>
                      <th style={{ padding: "0.75rem", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Opponent</th>
                      <th style={{ padding: "0.75rem", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>Result</th>
                      <th style={{ padding: "0.75rem", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>PTS</th>
                      <th style={{ padding: "0.75rem", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>REB</th>
                      <th style={{ padding: "0.75rem", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>AST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentGames.map((game, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #e2e8f0", transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f7fafc"} onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}>
                        <td style={{ padding: "0.75rem" }}>
                          {game.gameDate instanceof Date 
                            ? game.gameDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) 
                            : 'Unknown date'}
                        </td>
                        <td style={{ padding: "0.75rem" }}>{game.opponent || 'Unknown'}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0.25rem 0.5rem",
                            borderRadius: "9999px",
                            fontSize: "0.75rem",
                            fontWeight: "700",
                            backgroundColor: game.result === "W" ? "#c6f6d5" : "#fed7d7",
                            color: game.result === "W" ? "#22543d" : "#c53030"
                          }}>
                            {game.result} {game.score}
                          </span>
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "center", color: "#3182ce", fontWeight: "500" }}>{game.points || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{game.rebounds || 0}</td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>{game.assists || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ backgroundColor: "#f7fafc", borderRadius: "0.5rem", padding: "2rem", textAlign: "center" }}>
                <p style={{ color: "#718096", fontSize: "1rem" }}>No recent games found for this player with this team.</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Back to Team Button */}
      <div style={{ marginTop: "2rem", textAlign: "center" }}>
        <Link to={`/team/${teamId}`} style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0.75rem 1.5rem",
          backgroundColor: "#3182ce",
          color: "white",
          borderRadius: "0.5rem",
          textDecoration: "none",
          fontWeight: "500",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          transition: "background-color 0.2s"
        }}>
          Back to Team
        </Link>
      </div>
    </div>
  );
}

export default PlayerViewPage;
